/**
 * NavigatorX GDPR Compliance Dashboard
 *
 * Admin interface for managing privacy compliance:
 * - Data export requests (Right to Access)
 * - Deletion requests (Right to be Forgotten)
 * - Request status tracking and processing
 * - Admin notes and audit trail
 *
 * Service functions wired:
 * - createDataExportRequest, getDataExportRequests, processDataExport
 * - createDeletionRequest, getDeletionRequests, processDeletionRequest
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Shield,
  Download,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  FileText,
  User,
  Mail,
  Calendar,
  Eye,
  Search,
  Filter,
  X,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  createDataExportRequest,
  getDataExportRequests,
  processDataExport,
  createDeletionRequest,
  getDeletionRequests,
  processDeletionRequest,
  type DataExportRequest,
  type DeletionRequest,
} from '../../../utils/services/navigatorXAuditService';
import { getUser, timestampToDate } from '../../../utils/services/navigatorXService';
import { auth } from '../../../utils/firebase/client';

// ============================================================================
// MODULE-LEVEL PURE FUNCTIONS
// ============================================================================

type RequestStatus = DataExportRequest['status'] | DeletionRequest['status'];

function getStatusBadge(status: RequestStatus): { label: string; className: string } {
  switch (status) {
    case 'pending':
      return { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' };
    case 'processing':
      return { label: 'Processing', className: 'bg-blue-100 text-blue-800' };
    case 'completed':
      return { label: 'Completed', className: 'bg-green-100 text-green-800' };
    case 'failed':
      return { label: 'Failed', className: 'bg-red-100 text-red-800' };
    case 'rejected':
      return { label: 'Rejected', className: 'bg-gray-100 text-gray-800' };
    default:
      return { label: String(status), className: 'bg-gray-100 text-gray-600' };
  }
}

function formatTimestamp(ts: unknown): string {
  if (!ts) return '—';
  const date = timestampToDate(ts);
  if (!date) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

// ============================================================================
// COMPONENT TYPES
// ============================================================================

interface GdprComplianceDashboardProps {
  embedded?: boolean;
}

type ActiveTab = 'export' | 'deletion';
type FilterStatus = 'all' | 'pending' | 'processing' | 'completed' | 'failed' | 'rejected';

// ============================================================================
// COMPONENT
// ============================================================================

export default function GdprComplianceDashboard({ embedded }: GdprComplianceDashboardProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('export');
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');

  // Export requests
  const [exportRequests, setExportRequests] = useState<DataExportRequest[]>([]);
  const [exportLoading, setExportLoading] = useState(false);

  // Deletion requests
  const [deletionRequests, setDeletionRequests] = useState<DeletionRequest[]>([]);
  const [deletionLoading, setDeletionLoading] = useState(false);

  // Create new request
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createUserId, setCreateUserId] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createReason, setCreateReason] = useState('');
  const [creating, setCreating] = useState(false);

  // Process request
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [processNotes, setProcessNotes] = useState('');
  const [processDownloadUrl, setProcessDownloadUrl] = useState('');

  // Detail view
  const [selectedRequest, setSelectedRequest] = useState<DataExportRequest | DeletionRequest | null>(null);

  const adminId = auth.currentUser?.uid || 'unknown';

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [exports, deletions] = await Promise.all([
        getDataExportRequests(),
        getDeletionRequests(),
      ]);
      setExportRequests(exports);
      setDeletionRequests(deletions);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ============================================================================
  // CREATE HANDLERS
  // ============================================================================

  const handleCreateExportRequest = async () => {
    if (!createUserId.trim()) {
      toast.error('User ID is required');
      return;
    }

    setCreating(true);
    try {
      // Verify user exists
      const user = await getUser(createUserId.trim());
      if (!user) {
        toast.error('User not found');
        setCreating(false);
        return;
      }

      await createDataExportRequest(
        createUserId.trim(),
        createEmail.trim() || user.email || undefined
      );
      toast.success('Data export request created');
      setShowCreateForm(false);
      setCreateUserId('');
      setCreateEmail('');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCreating(false);
    }
  };

  const handleCreateDeletionRequest = async () => {
    if (!createUserId.trim()) {
      toast.error('User ID is required');
      return;
    }

    setCreating(true);
    try {
      const user = await getUser(createUserId.trim());
      if (!user) {
        toast.error('User not found');
        setCreating(false);
        return;
      }

      await createDeletionRequest(
        createUserId.trim(),
        createReason.trim() || undefined,
        createEmail.trim() || user.email || undefined
      );
      toast.success('Deletion request created');
      setShowCreateForm(false);
      setCreateUserId('');
      setCreateEmail('');
      setCreateReason('');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCreating(false);
    }
  };

  // ============================================================================
  // PROCESS HANDLERS
  // ============================================================================

  const handleProcessExport = async (requestId: string) => {
    setProcessingId(requestId);
    try {
      await processDataExport(requestId, adminId, processDownloadUrl.trim() || undefined);
      toast.success('Export request processed');
      setProcessingId(null);
      setProcessDownloadUrl('');
      setSelectedRequest(null);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
      setProcessingId(null);
    }
  };

  const handleProcessDeletion = async (requestId: string, approve: boolean) => {
    setProcessingId(requestId);
    try {
      await processDeletionRequest(requestId, approve, adminId, processNotes.trim() || undefined);
      toast.success(approve ? 'Deletion approved and processed' : 'Deletion request rejected');
      setProcessingId(null);
      setProcessNotes('');
      setSelectedRequest(null);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
      setProcessingId(null);
    }
  };

  // ============================================================================
  // COMPUTED
  // ============================================================================

  const pendingExports = exportRequests.filter(r => r.status === 'pending').length;
  const pendingDeletions = deletionRequests.filter(r => r.status === 'pending').length;

  const filteredExports = filterStatus === 'all'
    ? exportRequests
    : exportRequests.filter(r => r.status === filterStatus);

  const filteredDeletions = filterStatus === 'all'
    ? deletionRequests
    : deletionRequests.filter(r => r.status === filterStatus);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        <span className="ml-3 text-gray-500">Loading compliance data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600">Export Requests</p>
                <p className="text-2xl font-bold text-blue-700">{exportRequests.length}</p>
              </div>
              <Download className="h-6 w-6 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-br ${pendingExports > 0 ? 'from-yellow-50 to-yellow-100 border-yellow-300' : 'from-gray-50 to-gray-100'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-700">Pending Exports</p>
                <p className="text-2xl font-bold text-yellow-800">{pendingExports}</p>
              </div>
              <Clock className="h-6 w-6 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600">Deletion Requests</p>
                <p className="text-2xl font-bold text-red-700">{deletionRequests.length}</p>
              </div>
              <Trash2 className="h-6 w-6 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-br ${pendingDeletions > 0 ? 'from-orange-50 to-orange-100 border-orange-300' : 'from-gray-50 to-gray-100'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-700">Pending Deletions</p>
                <p className="text-2xl font-bold text-orange-800">{pendingDeletions}</p>
              </div>
              <AlertTriangle className="h-6 w-6 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs + Toolbar */}
      <Card>
        <CardContent className="p-0">
          {/* Tab Switcher */}
          <div className="flex border-b">
            <button
              onClick={() => { setActiveTab('export'); setFilterStatus('all'); }}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'export' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Download className="h-4 w-4" />
              Data Export Requests
              {pendingExports > 0 && (
                <Badge className="bg-yellow-100 text-yellow-800 text-xs ml-1">{pendingExports}</Badge>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('deletion'); setFilterStatus('all'); }}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'deletion' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Trash2 className="h-4 w-4" />
              Deletion Requests
              {pendingDeletions > 0 && (
                <Badge className="bg-red-100 text-red-800 text-xs ml-1">{pendingDeletions}</Badge>
              )}
            </button>
          </div>

          {/* Toolbar */}
          <div className="flex items-center justify-between p-4 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              {(['all', 'pending', 'completed', activeTab === 'deletion' ? 'rejected' : 'failed'] as FilterStatus[]).map(status => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(status)}
                  className="capitalize"
                >
                  {status}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={loadData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCreateForm(true)}
                className={activeTab === 'export' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {activeTab === 'export' ? (
                  <><Download className="h-4 w-4 mr-1" /> New Export Request</>
                ) : (
                  <><Trash2 className="h-4 w-4 mr-1" /> New Deletion Request</>
                )}
              </Button>
            </div>
          </div>

          {/* Request List */}
          {activeTab === 'export' ? (
            filteredExports.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <Download className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No export requests</p>
                <p className="text-sm mt-1">Data export requests will appear here</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredExports.map(request => {
                  const badge = getStatusBadge(request.status);
                  return (
                    <div
                      key={request.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedRequest(request)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium flex items-center gap-2">
                            <User className="h-3 w-3" />
                            {request.userId}
                          </p>
                          {request.userEmail && (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {request.userEmail}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(request.requestedAt)}
                        </span>
                        <Badge className={`text-xs ${badge.className}`}>{badge.label}</Badge>
                        {request.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRequest(request);
                            }}
                            className="text-blue-600 border-blue-300"
                          >
                            Process
                          </Button>
                        )}
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            filteredDeletions.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <Trash2 className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No deletion requests</p>
                <p className="text-sm mt-1">User deletion requests will appear here</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredDeletions.map(request => {
                  const badge = getStatusBadge(request.status);
                  return (
                    <div
                      key={request.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedRequest(request)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                          <Trash2 className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium flex items-center gap-2">
                            <User className="h-3 w-3" />
                            {request.userId}
                          </p>
                          {request.userEmail && (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {request.userEmail}
                            </p>
                          )}
                          {request.reason && (
                            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[300px]">
                              Reason: {request.reason}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(request.requestedAt)}
                        </span>
                        <Badge className={`text-xs ${badge.className}`}>{badge.label}</Badge>
                        {request.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProcessDeletion(request.id!, true);
                              }}
                              disabled={processingId === request.id}
                              className="bg-red-600 hover:bg-red-700 text-xs"
                            >
                              {processingId === request.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                'Approve'
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRequest(request);
                              }}
                              className="text-xs"
                            >
                              Review
                            </Button>
                          </div>
                        )}
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Create Request Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className={`p-4 text-white flex items-center gap-3 ${
              activeTab === 'export' ? 'bg-blue-600' : 'bg-red-600'
            }`}>
              {activeTab === 'export' ? <Download className="h-5 w-5" /> : <Trash2 className="h-5 w-5" />}
              <h3 className="font-semibold">
                {activeTab === 'export' ? 'New Data Export Request' : 'New Deletion Request'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                <Input
                  value={createUserId}
                  onChange={(e) => setCreateUserId(e.target.value)}
                  placeholder="Enter user ID..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                <Input
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  placeholder="User email for reference..."
                />
              </div>
              {activeTab === 'deletion' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <textarea
                    value={createReason}
                    onChange={(e) => setCreateReason(e.target.value)}
                    placeholder="Reason for deletion request..."
                    className="w-full px-3 py-2 border rounded-lg text-sm resize-none h-24 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
            <div className="border-t p-4 flex justify-end gap-3 bg-gray-50">
              <Button variant="outline" onClick={() => setShowCreateForm(false)} disabled={creating}>
                Cancel
              </Button>
              <Button
                onClick={activeTab === 'export' ? handleCreateExportRequest : handleCreateDeletionRequest}
                disabled={creating || !createUserId.trim()}
                className={activeTab === 'export' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {creating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</>
                ) : (
                  'Create Request'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Request Detail Sidebar */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-end z-50">
          <div className="bg-white h-full w-full max-w-lg shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Request Detail
              </h3>
              <button onClick={() => { setSelectedRequest(null); setProcessNotes(''); setProcessDownloadUrl(''); }}>
                <X className="h-5 w-5 text-gray-500 hover:text-gray-700" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                <Badge className={`text-sm ${getStatusBadge(selectedRequest.status).className}`}>
                  {getStatusBadge(selectedRequest.status).label}
                </Badge>
                <span className="text-sm text-gray-500">
                  {'downloadUrl' in selectedRequest ? 'Data Export' : 'Deletion Request'}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-500">Request ID</span>
                  <span className="text-sm font-mono">{selectedRequest.id}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-500">User ID</span>
                  <span className="text-sm font-mono">{selectedRequest.userId}</span>
                </div>
                {selectedRequest.userEmail && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-sm text-gray-500">Email</span>
                    <span className="text-sm">{selectedRequest.userEmail}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-500">Requested</span>
                  <span className="text-sm">{formatTimestamp(selectedRequest.requestedAt)}</span>
                </div>
                {selectedRequest.completedAt && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-sm text-gray-500">Completed</span>
                    <span className="text-sm">{formatTimestamp(selectedRequest.completedAt)}</span>
                  </div>
                )}
                {selectedRequest.processedBy && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-sm text-gray-500">Processed By</span>
                    <span className="text-sm font-mono">{selectedRequest.processedBy}</span>
                  </div>
                )}
                {'reason' in selectedRequest && selectedRequest.reason && (
                  <div className="py-2 border-b">
                    <span className="text-sm text-gray-500 block mb-1">Reason</span>
                    <p className="text-sm bg-gray-50 p-3 rounded-lg">{selectedRequest.reason}</p>
                  </div>
                )}
                {'notes' in selectedRequest && selectedRequest.notes && (
                  <div className="py-2 border-b">
                    <span className="text-sm text-gray-500 block mb-1">Admin Notes</span>
                    <p className="text-sm bg-gray-50 p-3 rounded-lg">{selectedRequest.notes}</p>
                  </div>
                )}
                {'downloadUrl' in selectedRequest && selectedRequest.downloadUrl && (
                  <div className="py-2 border-b">
                    <span className="text-sm text-gray-500 block mb-1">Download URL</span>
                    <a
                      href={selectedRequest.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 break-all"
                    >
                      {selectedRequest.downloadUrl}
                    </a>
                    {'expiresAt' in selectedRequest && selectedRequest.expiresAt && (
                      <p className="text-xs text-gray-400 mt-1">
                        Expires: {formatTimestamp(selectedRequest.expiresAt)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Action Section for Pending Requests */}
              {selectedRequest.status === 'pending' && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-sm mb-3">Process Request</h4>

                  {'downloadUrl' in selectedRequest || !('reason' in selectedRequest) ? (
                    // Export request processing
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Download URL (optional)</label>
                        <Input
                          value={processDownloadUrl}
                          onChange={(e) => setProcessDownloadUrl(e.target.value)}
                          placeholder="https://storage.example.com/export/..."
                        />
                        <p className="text-xs text-gray-400 mt-1">Link expires after 7 days</p>
                      </div>
                      <Button
                        onClick={() => handleProcessExport(selectedRequest.id!)}
                        disabled={processingId === selectedRequest.id}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        {processingId === selectedRequest.id ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                        ) : (
                          <><CheckCircle className="h-4 w-4 mr-2" /> Mark as Completed</>
                        )}
                      </Button>
                    </div>
                  ) : (
                    // Deletion request processing
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Admin Notes (optional)</label>
                        <textarea
                          value={processNotes}
                          onChange={(e) => setProcessNotes(e.target.value)}
                          placeholder="Notes about this decision..."
                          className="w-full px-3 py-2 border rounded-lg text-sm resize-none h-20 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex gap-2">
                          <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-red-800">
                            Approving will permanently delete all user data including their account,
                            favorites, history, referrals, and notifications. This cannot be undone.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={() => handleProcessDeletion(selectedRequest.id!, false)}
                          disabled={processingId === selectedRequest.id}
                          className="flex-1"
                        >
                          {processingId === selectedRequest.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><XCircle className="h-4 w-4 mr-2" /> Reject</>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleProcessDeletion(selectedRequest.id!, true)}
                          disabled={processingId === selectedRequest.id}
                          className="flex-1 bg-red-600 hover:bg-red-700"
                        >
                          {processingId === selectedRequest.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><Trash2 className="h-4 w-4 mr-2" /> Approve Deletion</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
